import { useEditor, type Editor } from '@tiptap/vue-3'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import { computed, nextTick, onBeforeUnmount, ref, useId, watch, type Ref } from 'vue'
import { sanitizeOrgRichText } from '~/composables/ui/sanitizeOrgRichText'
import {
  isEmptyOrgRichText,
  normalizeOrgRichTextInput,
  visibleTextLength,
} from '~/utils/orgRichText'
import iconBold from '~/assets/icons/rte/bold.svg'
import iconBullet from '~/assets/icons/rte/bullet.svg'
import iconClean from '~/assets/icons/rte/clean.svg'
import iconItalic from '~/assets/icons/rte/italic.svg'
import iconLink from '~/assets/icons/rte/link.svg'
import iconOrdered from '~/assets/icons/rte/ordered.svg'
import iconQuote from '~/assets/icons/rte/quote.svg'
import iconStrike from '~/assets/icons/rte/strikethrough.svg'
import iconUnderline from '~/assets/icons/rte/underline.svg'

export type SourceRichTextHeadingValue = 'paragraph' | '2' | '3'

export type SourceRichTextTool = {
  id: string
  title: string
  iconUrl: string
  pressed: boolean
  run: () => void
}

export type SourceRichTextEditorProps = {
  label: string
  theme: 'light' | 'dark'
  size: 'xsmall' | 'small' | 'medium'
  disabled: boolean
  readonly: boolean
  placeholder: string
  modelValue: string
  maxChars: number
  error: string
  helper: string
  hasCounter: boolean
  name?: string
  previewHover: boolean
}

type SourceRichTextEditorEmit = (event: 'update:modelValue', value: string) => void

type LinkPopoverBridge = {
  onLinkClick: () => void
  isLinkPopoverOpen: Ref<boolean>
}

function htmlFromEditor(rawHtml: string): string {
  const sanitized = sanitizeOrgRichText(rawHtml)
  return isEmptyOrgRichText(sanitized) ? '' : sanitized
}

function richTextFieldMeta(props: SourceRichTextEditorProps, instanceId: string) {
  const labelId = computed(function computeLabelId() {
    const slug = (props.name || props.label).replace(/\s+/g, '_').toLowerCase()
    return `${slug}-${instanceId}-rte-label`
  })
  const hintId = computed(function computeHintId() {
    return `${labelId.value}-hint`
  })
  const hintText = computed(function computeHintText() {
    return props.error?.trim() ? props.error.trim() : (props.helper ?? '').trim()
  })
  const visualSize = computed(function computeVisualSize() {
    return props.size === 'xsmall' ? 'small' : props.size
  })
  const isInteractive = computed(function computeIsInteractive() {
    return !props.disabled && !props.readonly
  })
  return { labelId, hintId, hintText, visualSize, isInteractive }
}

function createRichTextEditor(
  props: SourceRichTextEditorProps,
  emit: SourceRichTextEditorEmit,
  labelId: Ref<string>,
  isInteractive: Ref<boolean>,
  applyingExternal: Ref<boolean>,
) {
  return useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        code: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        protocols: ['http', 'https', 'mailto', 'tel'],
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Placeholder.configure({
        placeholder: props.placeholder,
      }),
    ],
    content: normalizeOrgRichTextInput(props.modelValue),
    editable: isInteractive.value,
    editorProps: {
      attributes: {
        class: 'source-rte__prose',
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-labelledby': labelId.value,
      },
      transformPastedHTML: function transformPastedHTML(html: string) {
        return sanitizeOrgRichText(html)
      },
    },
    onUpdate: function onEditorUpdate({ editor: instance }) {
      if (applyingExternal.value) {
        return
      }
      const visible = instance.getText({ blockSeparator: '\n' }).trim()
      if (visible.length > props.maxChars) {
        instance.commands.undo()
        return
      }
      emit('update:modelValue', htmlFromEditor(instance.getHTML()))
    },
  })
}

function watchRichTextEditor(
  props: SourceRichTextEditorProps,
  editor: Ref<Editor | undefined>,
  applyingExternal: Ref<boolean>,
  canEdit: Ref<boolean>,
) {
  watch(
    function watchModelValue() {
      return props.modelValue
    },
    function onModelValueChanged(next) {
      const instance = editor.value
      if (!instance) {
        return
      }
      const nextHtml = htmlFromEditor(normalizeOrgRichTextInput(next ?? ''))
      const currentHtml = htmlFromEditor(instance.getHTML())
      if (nextHtml === currentHtml) {
        return
      }
      applyingExternal.value = true
      instance.commands.setContent(nextHtml || '<p></p>', { emitUpdate: false })
      void nextTick(function clearExternalApplyFlag() {
        applyingExternal.value = false
      })
    },
  )

  watch(
    function watchEditableFlags() {
      return [props.disabled, props.readonly] as const
    },
    function onEditableFlagsChanged() {
      editor.value?.setEditable(canEdit.value)
    },
  )
}

function richTextCommands(editor: Ref<Editor | undefined>, canEdit: Ref<boolean>) {
  function run(command: (instance: Editor) => void): void {
    const instance = editor.value
    if (!instance || !canEdit.value) {
      return
    }
    command(instance)
  }

  function toggleMark(name: 'bold' | 'italic' | 'underline' | 'strike'): void {
    run(function applyMark(instance) {
      instance.chain().focus().toggleMark(name).run()
    })
  }

  function onHeadingChange(value: string): void {
    const heading = value as SourceRichTextHeadingValue
    run(function applyHeading(instance) {
      if (heading === 'paragraph') {
        instance.chain().focus().setParagraph().run()
        return
      }
      instance.chain().focus().toggleHeading({ level: Number(heading) as 2 | 3 }).run()
    })
  }

  return {
    toggleMark,
    toggleBulletList: () => run((instance) => {
      instance.chain().focus().toggleBulletList().run()
    }),
    toggleOrderedList: () => run((instance) => {
      instance.chain().focus().toggleOrderedList().run()
    }),
    toggleBlockquote: () => run((instance) => {
      instance.chain().focus().toggleBlockquote().run()
    }),
    onHeadingChange,
    clearFormat: () => run((instance) => {
      instance.chain().focus().unsetAllMarks().clearNodes().run()
    }),
    isActive: (name: string, attributes?: Record<string, unknown>) =>
      Boolean(editor.value?.isActive(name, attributes)),
  }
}

function richTextToolbars(
  commands: ReturnType<typeof richTextCommands>,
  linkPopover: LinkPopoverBridge,
) {
  const inlineTools = computed<SourceRichTextTool[]>(function computeInlineTools() {
    return [
      { id: 'bold', title: 'Gras', iconUrl: iconBold, pressed: commands.isActive('bold'), run: () => commands.toggleMark('bold') },
      { id: 'italic', title: 'Italique', iconUrl: iconItalic, pressed: commands.isActive('italic'), run: () => commands.toggleMark('italic') },
      { id: 'underline', title: 'Souligné', iconUrl: iconUnderline, pressed: commands.isActive('underline'), run: () => commands.toggleMark('underline') },
      { id: 'strike', title: 'Barré', iconUrl: iconStrike, pressed: commands.isActive('strike'), run: () => commands.toggleMark('strike') },
    ]
  })
  const blockTools = computed<SourceRichTextTool[]>(function computeBlockTools() {
    return [
      { id: 'bullet', title: 'Liste à puces', iconUrl: iconBullet, pressed: commands.isActive('bulletList'), run: commands.toggleBulletList },
      { id: 'ordered', title: 'Liste numérotée', iconUrl: iconOrdered, pressed: commands.isActive('orderedList'), run: commands.toggleOrderedList },
      { id: 'quote', title: 'Citation', iconUrl: iconQuote, pressed: commands.isActive('blockquote'), run: commands.toggleBlockquote },
    ]
  })
  const embedTools = computed<SourceRichTextTool[]>(function computeEmbedTools() {
    return [
      {
        id: 'link',
        title: 'Lien',
        iconUrl: iconLink,
        pressed: commands.isActive('link') || linkPopover.isLinkPopoverOpen.value,
        run: linkPopover.onLinkClick,
      },
      { id: 'clean', title: 'Effacer le format', iconUrl: iconClean, pressed: false, run: commands.clearFormat },
    ]
  })
  return { inlineTools, blockTools, embedTools }
}

export function useSourceRichTextEditor(
  props: SourceRichTextEditorProps,
  emit: SourceRichTextEditorEmit,
  linkPopover: LinkPopoverBridge
) {
  const { labelId, hintId, hintText, visualSize, isInteractive } = richTextFieldMeta(props, useId())
  const applyingExternal = ref(false)
  const rteRootEl = ref<HTMLElement | null>(null)
  const editor = createRichTextEditor(props, emit, labelId, isInteractive, applyingExternal)
  const editorReady = computed(function computeEditorReady() {
    return Boolean(editor.value)
  })
  const headingValue = computed<SourceRichTextHeadingValue>(function computeHeadingValue() {
    const instance = editor.value
    if (!instance) {
      return 'paragraph'
    }
    if (instance.isActive('heading', { level: 2 })) {
      return '2'
    }
    if (instance.isActive('heading', { level: 3 })) {
      return '3'
    }
    return 'paragraph'
  })
  const visibleCount = computed(function computeVisibleCount() {
    return visibleTextLength(props.modelValue ?? '')
  })
  const isFilled = computed(function computeIsFilled() {
    return !isEmptyOrgRichText(props.modelValue ?? '')
  })
  const canEdit = computed(function computeCanEdit() {
    return isInteractive.value
  })
  watchRichTextEditor(props, editor, applyingExternal, canEdit)
  const commands = richTextCommands(editor, canEdit)
  const { inlineTools, blockTools, embedTools } = richTextToolbars(commands, linkPopover)

  onBeforeUnmount(function destroyEditor() {
    editor.value?.destroy()
  })

  return {
    rteRootEl,
    editor,
    editorReady,
    headingValue,
    visibleCount,
    isFilled,
    canEdit,
    labelId,
    hintId,
    hintText,
    visualSize,
    inlineTools,
    blockTools,
    embedTools,
    onHeadingChange: commands.onHeadingChange,
  }
}
